import React, { useContext, useReducer } from 'react'
import Cookies from 'js-cookie'
import { appEnvironment } from 'configs'
import { clearScreenCache } from './screenCache'
import { createLoginCookies } from './loginCookies'

export const AppContext = React.createContext()

export const getLoginIfCustom = () => {
  let matches = window.location.hostname.match(
    /([^.]+)[.](bookfocal\.localhost|dev\.bookfocal\.com|bookfocal\.com)/,
  )

  if (matches && matches[1] !== 'app') {
    return matches[1]
  }
}

export const getDomainIfCustom = () => {
  let matches = window.location.hostname.match(
    /([^.]+[.])(bookfocal\.localhost|dev\.bookfocal\.com|bookfocal\.com)/,
  )

  if (!matches && window.location.hostname !== 'localhost') {
    return window.location.hostname
  }
}

export const AuthState = () => {
  const token = getToken()
  const user = getUser()
  let isAuthed = false
  if (token !== '') {
    isAuthed = true
  }
  return {
    isAuthed: isAuthed,
    token: token,
    user: user,
    isMobile: false,
    scrolledToBottom: false,
    photographer: {},
    portfolios: [],
    packages: [],
    /* True once a package list has arrived. Screens render before it does now (Admin.jsx), and
       an empty `packages` alone cannot tell "not loaded yet" from "this account has none". */
    packagesLoaded: false,
    alertText: [],
    alertType: 'error',
    portfolioSave: false,
    calendarIsAuthed: false,
    name: '',
    stripePlans: [],
  }
}

export const AuthProvider = ({ reducer, initialState, children }) => (
  <AppContext.Provider value={useReducer(reducer, initialState)}>{children}</AppContext.Provider>
)

export const useAppContext = () => useContext(AppContext)

export const reducer = (state, action) => {
  if (action.type === 'setAuth') {
    setToken(action.token)
    return {
      ...state,
      isAuthed: action.isAuthed,
      token: action.token,
    }
  } else if (action.type === 'setUser') {
    setUser(action.user)
    return {
      ...state,
      user: action.user,
    }
  } else if (action.type === 'logout') {
    deleteToken()
    deleteUser()
    // What the admin screens last showed. Keyed by token already; cleared too, because some
    // logouts push the login route instead of reloading, and nothing of the account should stay.
    clearScreenCache()
    return {}
  } else if (action.type === 'setPhotographer') {
    return {
      ...state,
      photographer: action.photographer,
    }
  } else if (action.type === 'setPortfolios') {
    return {
      ...state,
      portfolios: action.portfolios,
    }
  } else if (action.type === 'setPackages') {
    return {
      ...state,
      packages: action.packages,
      packagesLoaded: true,
    }
  } else if (action.type === 'setMobile') {
    return {
      ...state,
      isMobile: action.isMobile,
    }
  } else if (action.type === 'setScrolled') {
    return {
      ...state,
      scrolledToBottom: action.scrolledToBottom,
    }
  } else if (action.type === 'setAlertText') {
    return {
      ...state,
      alertText: action.alertText,
    }
  } else if (action.type === 'setAlertType') {
    return {
      ...state,
      alertType: action.alertType,
    }
  } else if (action.type === 'setPortfolioSave') {
    return {
      ...state,
      portfolioSave: action.portfolioSave,
    }
  } else if (action.type === 'setCalendarIsAuthed') {
    return {
      ...state,
      calendarIsAuthed: action.calendarIsAuthed,
    }
  } else if (action.type === 'setName') {
    return {
      ...state,
      name: action.name,
    }
  } else if (action.type === 'setStripePlans') {
    return {
      ...state,
      stripePlans: action.stripePlans,
    }
  } else {
    throw new Error('invalid type: ' + action.type)
  }
}

let top_level_domain = window.location.hostname.replace(/(.*?)\.(dev\.)?([^.]+\.[^.]+)$/, '.$2$3')

let token_cookie_suffix = top_level_domain.match(/\.dev\./) ? '_dev' : ''

const TOKEN_COOKIE = 'bf_token' + token_cookie_suffix
const USER_COOKIE = 'bf_user' + token_cookie_suffix

// Host-only, so other bookfocal.com hosts can't read the token (see loginCookies.js).
// top_level_domain is now used only to find and remove copies older builds left there.
const loginCookies = createLoginCookies({
  Cookies,
  parentDomain: top_level_domain,
  secure: appEnvironment === 'production' || appEnvironment === 'development',
})
loginCookies.migrate(TOKEN_COOKIE)
loginCookies.migrate(USER_COOKIE)

const setToken = (token) => {
  loginCookies.set(TOKEN_COOKIE, token)
}

const setUser = (user) => {
  loginCookies.set(USER_COOKIE, JSON.stringify(user))
}

const getToken = () => {
  return loginCookies.get(TOKEN_COOKIE) || ''
}

export const getUser = () => {
  const user = loginCookies.get(USER_COOKIE)
  return user ? JSON.parse(user) : {}
}

const deleteToken = () => {
  loginCookies.remove(TOKEN_COOKIE)
}

const deleteUser = () => {
  loginCookies.remove(USER_COOKIE)
}
